import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions, seededRandom } from "../spec";
import { sum } from "./estadistica";

const SHEET = "MT Inventario";
const TABLE = "InventarioMT";

const CATEGORIAS = ["Herramienta", "Eléctrico", "Plomería", "Pintura"] as const;
const ALMACENES = ["Norte", "Centro", "Sur"] as const;

const rnd = seededRandom(7272);
// Producto, Categoría, Almacén, Existencia, Mínimo, Costo, Rotación (días)
const ROWS = Array.from({ length: 32 }, (_, i) => [
  `SKU-${401 + i}`,
  CATEGORIAS[i % CATEGORIAS.length],
  rnd.pick(ALMACENES),
  rnd.int(0, 400),
  rnd.pick([20, 30, 50, 80]),
  rnd.int(15, 950),
  rnd.int(5, 120),
] as [string, string, string, number, number, number, number]);

type Row = (typeof ROWS)[number];
const IN = { categoria: "Eléctrico", almacen: "Norte" };
const valor = (r: Row) => r[3] * r[5];
const valorTotal = sum(ROWS.map(valor));

export const spec: FuncionProjectSpec = {
  projectId: "funciones-sumaproducto",
  sheets: [
    {
      name: SHEET,
      title: "Inventario valorizado",
      subtitle: "Existencias en tres almacenes — calcula valores y promedios ponderados sin columnas auxiliares",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Producto", "Categoría", "Almacén", "Existencia", "Mínimo", "Costo", "Rotación"],
          rows: ROWS,
          formats: { Costo: "$#,##0" },
        },
      ],
      inputs: [
        { address: "K4", label: "Categoría", value: IN.categoria },
        { address: "K5", label: "Almacén", value: IN.almacen },
      ],
      answers: [
        { address: "K8", label: "Tarea 1 · Valor del inventario", format: "$#,##0" },
        { address: "K9", label: "Tarea 2 · Valor de la categoría", format: "$#,##0" },
        { address: "K10", label: "Tarea 3 · Categoría en el almacén", format: "$#,##0" },
        { address: "K11", label: "Tarea 4 · Costo promedio ponderado", format: "$#,##0.00" },
        { address: "K12", label: "Tarea 5 · Productos bajo mínimo" },
        { address: "K13", label: "Tarea 6 · Rotación ponderada (días)", format: "0.0" },
      ],
    },
  ],
  checks: {
    1: { kind: "cell", sheet: SHEET, address: "K8", fns: ["SUMPRODUCT"], expected: valorTotal },
    2: {
      kind: "cell", sheet: SHEET, address: "K9", fns: ["SUMPRODUCT"],
      expected: sum(ROWS.filter((r) => r[1] === IN.categoria).map(valor)),
    },
    3: {
      kind: "cell", sheet: SHEET, address: "K10", fns: ["SUMPRODUCT"],
      expected: sum(ROWS.filter((r) => r[1] === IN.categoria && r[2] === IN.almacen).map(valor)),
    },
    4: {
      kind: "cell", sheet: SHEET, address: "K11", fns: ["SUMPRODUCT", "SUM"],
      expected: valorTotal / sum(ROWS.map((r) => r[3])),
    },
    5: {
      kind: "cell", sheet: SHEET, address: "K12", fns: ["SUMPRODUCT"],
      expected: ROWS.filter((r) => r[3] < r[4]).length,
    },
    6: {
      kind: "cell", sheet: SHEET, address: "K13", fns: ["SUMPRODUCT"], minCounts: { SUMPRODUCT: 2 },
      expected: sum(ROWS.map((r) => valor(r) * r[6])) / valorTotal,
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-sumaproducto",
  title: "SUMAPRODUCTO: Inventario valorizado",
  subtitle: "Multiplicar y sumar en un solo paso, con condiciones",
  description:
    "Tienes 32 productos en tres almacenes. Contabilidad necesita el valor del inventario (existencia × costo), por categoría y por almacén, el costo promedio ponderado y cuántos días tarda en rotar el inventario.\n\nSUMAPRODUCTO multiplica las columnas fila por fila y suma los resultados, sin necesidad de una columna auxiliar. Además, si multiplicas por una condición (VERDADERO = 1, FALSO = 0), solo cuentan las filas que la cumplen.",
  topics: [
    "SUMAPRODUCTO(rango1, rango2)",
    "Condiciones dentro de SUMAPRODUCTO: (rango=valor)",
    "Promedio ponderado",
    "Contar filas que cumplen una comparación entre columnas",
    "Ponderar por valor, no por cantidad",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Valor del inventario",
      description:
        "En la hoja <strong>MT Inventario</strong>, celda <strong>K8</strong>: el valor total del inventario, es decir, la suma de <strong>Existencia × Costo</strong> de todos los productos.",
      solution: [
        `Selecciona ${F("K8")}.`,
        "SUMAPRODUCTO multiplica Existencia por Costo en cada fila y suma todos los productos.",
        `Escribe ${F("=SUMAPRODUCTO(InventarioMT[Existencia],InventarioMT[Costo])")}.`,
      ],
      expectedResult: "Valor total en pesos",
      finalFormula: "=SUMAPRODUCTO(InventarioMT[Existencia],InventarioMT[Costo])",
    },
    {
      id: 2,
      level: "intermedio",
      title: "Tarea 2: Valor de una categoría",
      description: "En <strong>K9</strong>: el valor del inventario solo de la categoría de <strong>K4</strong>.",
      solution: [
        `Selecciona ${F("K9")}.`,
        `${F("(InventarioMT[Categoría]=K4)")} da VERDADERO o FALSO por fila; al multiplicarlo se convierte en 1 o 0 y deja fuera las otras categorías.`,
        `Escribe ${F("=SUMAPRODUCTO((InventarioMT[Categoría]=K4)*InventarioMT[Existencia]*InventarioMT[Costo])")}.`,
      ],
      expectedResult: "Valor de los productos eléctricos",
      finalFormula: "=SUMAPRODUCTO((InventarioMT[Categoría]=K4)*InventarioMT[Existencia]*InventarioMT[Costo])",
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Categoría en un almacén",
      description: conditions("En <strong>K10</strong>: el valor del inventario que cumpla <strong>las dos</strong> condiciones:", [
        "Categoría de <strong>K4</strong>",
        "Almacén de <strong>K5</strong>",
      ]),
      solution: [
        `Selecciona ${F("K10")}.`,
        "Cada condición extra es otro factor entre paréntesis.",
        `Escribe ${F("=SUMAPRODUCTO((InventarioMT[Categoría]=K4)*(InventarioMT[Almacén]=K5)*InventarioMT[Existencia]*InventarioMT[Costo])")}.`,
      ],
      expectedResult: "Valor de eléctricos en el almacén Norte",
      finalFormula:
        "=SUMAPRODUCTO((InventarioMT[Categoría]=K4)*(InventarioMT[Almacén]=K5)*InventarioMT[Existencia]*InventarioMT[Costo])",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Costo promedio ponderado",
      description:
        "En <strong>K11</strong>: el costo promedio <strong>por pieza</strong> del inventario: valor total ÷ total de piezas. No es lo mismo que PROMEDIO(Costo), porque los productos con más existencia deben pesar más.",
      solution: [
        `Selecciona ${F("K11")}.`,
        "Promedio ponderado = SUMAPRODUCTO(valores, pesos) ÷ SUMA(pesos). Aquí el peso es la existencia.",
        `Escribe ${F("=SUMAPRODUCTO(InventarioMT[Existencia],InventarioMT[Costo])/SUMA(InventarioMT[Existencia])")}.`,
        `Compara con ${F("=PROMEDIO(InventarioMT[Costo])")}: la diferencia muestra por qué ponderar importa.`,
      ],
      expectedResult: "Costo promedio por pieza",
      finalFormula: "=SUMAPRODUCTO(InventarioMT[Existencia],InventarioMT[Costo])/SUMA(InventarioMT[Existencia])",
    },
    {
      id: 5,
      level: "reto",
      title: "Tarea 5: Productos bajo el mínimo",
      description:
        "En <strong>K12</strong>: cuántos productos tienen <strong>Existencia menor que su Mínimo</strong>. CONTAR.SI.CONJUNTO no puede comparar dos columnas entre sí; SUMAPRODUCTO sí.",
      solution: [
        `Selecciona ${F("K12")}.`,
        `${F("--(…)")} (doble menos) convierte VERDADERO/FALSO en 1/0 para poder sumarlos.`,
        `Escribe ${F("=SUMAPRODUCTO(--(InventarioMT[Existencia]<InventarioMT[Mínimo]))")}.`,
      ],
      expectedResult: "Número de productos a resurtir",
      finalFormula: "=SUMAPRODUCTO(--(InventarioMT[Existencia]<InventarioMT[Mínimo]))",
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Rotación ponderada por valor",
      description: conditions(
        "En <strong>K13</strong>: los días de rotación promedio del inventario, <strong>ponderados por el valor</strong> de cada producto (los productos que valen más pesan más):",
        [
          "Arriba: SUMAPRODUCTO de Existencia × Costo × Rotación",
          "Abajo: el valor total (SUMAPRODUCTO de Existencia × Costo)",
        ]
      ),
      solution: [
        `Selecciona ${F("K13")}.`,
        `Escribe ${F("=SUMAPRODUCTO(InventarioMT[Existencia]*InventarioMT[Costo]*InventarioMT[Rotación])/SUMAPRODUCTO(InventarioMT[Existencia],InventarioMT[Costo])")}.`,
      ],
      expectedResult: "Días promedio de rotación del dinero invertido",
      finalFormula:
        "=SUMAPRODUCTO(InventarioMT[Existencia]*InventarioMT[Costo]*InventarioMT[Rotación])/SUMAPRODUCTO(InventarioMT[Existencia],InventarioMT[Costo])",
    },
  ],
};
